import React from 'react';
import type { LotteryNumber } from '../types/lottery';

interface HeatMapProps {
  numbers: LotteryNumber[];
}

export default function HeatMap({ numbers }: HeatMapProps) {
  const getHeatColor = (probability: number) => {
    if (probability >= 0.8) return 'bg-gradient-to-br from-blue-800 to-blue-900';
    if (probability >= 0.6) return 'bg-gradient-to-br from-blue-600 to-blue-700';
    if (probability >= 0.4) return 'bg-gradient-to-br from-blue-400 to-blue-500';
    if (probability >= 0.2) return 'bg-gradient-to-br from-blue-300 to-blue-400';
    return 'bg-gradient-to-br from-blue-100 to-blue-200';
  };

  // Separate main numbers and Powerball numbers
  const mainNumbers = numbers.slice(0, 69);
  const powerballNumbers = numbers.slice(69);

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-6 border border-gray-100">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Main Numbers (1-69)</h3>
      <div className="grid grid-cols-5 sm:grid-cols-7 lg:grid-cols-10 gap-1 sm:gap-2 mb-8">
        {mainNumbers.map((num) => (
          <div
            key={num.number}
            className={`aspect-square rounded-lg ${getHeatColor(num.probability)} 
              flex flex-col items-center justify-center p-2 shadow-sm
              transform hover:scale-105 transition-transform duration-200
              border border-white/10 text-center`}
          >
            <span className="text-base sm:text-lg font-bold text-white">{num.number}</span>
            <span className="text-[10px] sm:text-xs text-white/90">{(num.probability * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
      
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Powerball Numbers (1-26)</h3>
      <div className="grid grid-cols-5 sm:grid-cols-7 lg:grid-cols-13 gap-1 sm:gap-2">
        {powerballNumbers.map((num) => (
          <div
            key={num.number}
            className={`aspect-square rounded-lg ${getHeatColor(num.probability)} 
              flex flex-col items-center justify-center p-2
              border-2 border-white/20 shadow-sm
              transform hover:scale-105 transition-transform duration-200
              backdrop-blur-sm backdrop-filter`}
          >
            <span className="text-base sm:text-lg font-bold text-white">{num.number}</span>
            <span className="text-[10px] sm:text-xs text-white/90">{(num.probability * 100).toFixed(1)}%</span>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-center space-x-4">
          <span className="text-sm text-gray-600">Probability Scale:</span>
          <div className="flex items-center space-x-1 sm:space-x-2">
            {[0.1, 0.3, 0.5, 0.7, 0.9].map((prob) => (
              <div
                key={prob}
                className={`w-6 h-6 sm:w-8 sm:h-8 rounded-lg ${getHeatColor(prob)} flex items-center justify-center border border-white/20`}
              >
                <span className="text-[10px] sm:text-xs text-white">{(prob * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}